import { Box, Typography, IconButton } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DialogContainer from "../../../components/dialog-container/dialog-container";
import Table from "../../../components/table/table";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import TableHeaderTextField from "../../../components/table-header-text-filed/table-header-text-filed";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getInvoiceOrdersRequest } from "../../../redux/orders/slice";

interface InvoiceLineItemsDialogProps {
  showDialog: boolean;
  closeDialog: () => void;
  invoiceId: string;
  invoiceNumber: string;
}

export default function InvoiceLineItemsDialog({
  showDialog,
  closeDialog,
  invoiceId,
  invoiceNumber,
}: InvoiceLineItemsDialogProps) {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({
    invoiceId: "",
    page: 1,
    pageSize: 10,
    invoiceLineItemNumber: "",
    orderNumber: "",
    totalAmount: "",
    sortColumn: "",
    sortOrder: "",
  });

  const { loading, invoiceOrders } = useSelector((state: any) => state.orders);

  const rowCountRef = useRef(invoiceOrders?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (invoiceOrders?.totalCount !== undefined) {
      rowCountRef.current = invoiceOrders?.totalCount;
    }
    return rowCountRef.current;
  }, [invoiceOrders?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "invoiceLineItemNumber",
      headerName: "Invoice Line Item #",
      width: 300,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
          {params.row.invoiceLineItemNumber || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="invoiceLineItemNumber"
          sortField="invoiceLineItemNumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "orderNumber",
      headerName: "Order #",
      width: 300,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
          {params.row.orderNumber || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="orderNumber"
          sortField="orderNumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      width: 300,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "bold", pt: 2 }}>
          ${params.row.totalAmount || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="totalAmount"
          sortField="totalAmount"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
  ];

  useEffect(() => {
    if (showDialog && invoiceId) {
      setPaginationModel({
        page: 0,
        pageSize: 10,
      });
      setFilterParams({
        invoiceId,
        page: 1,
        pageSize: 10,
        invoiceLineItemNumber: "",
        orderNumber: "",
        totalAmount: "",
        sortColumn: "",
        sortOrder: "",
      });
      setRowData([]);
    } else if (!showDialog) {
      setFilterParams({
        invoiceId: "",
        page: 1,
        pageSize: 10,
        invoiceLineItemNumber: "",
        orderNumber: "",
        totalAmount: "",
        sortColumn: "",
        sortOrder: "",
      });
      setRowData([]);
    }
  }, [showDialog, invoiceId]); // eslint-disable-line

  useEffect(() => {
    setRowData(invoiceOrders?.items || []);
  }, [invoiceOrders]);

  useEffect(() => {
    if (paginationModel) {
      setFilterParams((prev) => ({
        ...prev,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }));
    }
  }, [paginationModel]); // eslint-disable-line

  useEffect(() => {
    if (!showDialog || !invoiceId) {
      return;
    }

    const filters: any = { ...filterParams };
    const textFilterFields = [
      "invoiceLineItemNumber",
      "orderNumber",
      "totalAmount",
    ];
    textFilterFields.forEach((field) => {
      if (filters.hasOwnProperty(field) && filters[field] === "") {
        delete filters[field];
      }
    });

    // Remove empty sort parameters
    if (filters.sortColumn === "" || filters.sortOrder === "") {
      delete filters.sortColumn;
      delete filters.sortOrder;
    }

    setRowData([]);
    rowCountRef.current = 0;

    dispatch(
      getInvoiceOrdersRequest({
        invoiceId: invoiceId,
        params: {
          page: filters.page,
          pageSize: filters.pageSize,
          invoiceLineItemNumber: filters.invoiceLineItemNumber,
          orderNumber: filters.orderNumber,
          totalAmount: filters.totalAmount,
          sortColumn: filters.sortColumn,
          sortOrder: filters.sortOrder,
        },
      })
    );
    /* eslint-disable */
  }, [
    showDialog,
    invoiceId,
    filterParams.invoiceId,
    filterParams.page,
    filterParams.pageSize,
    filterParams.invoiceLineItemNumber,
    filterParams.orderNumber,
    filterParams.totalAmount,
    filterParams.sortColumn,
    filterParams.sortOrder,
  ]); /* eslint-enable */

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterItems = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  return (
    <DialogContainer
      title={`Invoice #${invoiceNumber}`}
      showDialog={showDialog}
      closeDialog={closeDialog}
      buttonText="DONE"
      onSubmit={closeDialog}
      cancelBtnRequired={false}
    >
      <Box sx={{ p: 0, overflow: "hidden" }}>
        <Box
          className="clients-header"
          sx={{
            mt: 3,
            mb: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
          }}
        >
          <Box display={"flex"} alignItems={"center"}>
            {/* Empty space for left side */}
          </Box>
          <Box>
            <IconButton
              aria-label="filter"
              onClick={() => onFilterClick(!showTableFilter)}
            >
              {showTableFilter ? (
                <FilterAltOutlinedIcon color="primary" />
              ) : (
                <FilterAltOutlinedIcon color="primary" />
              )}
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ mx: 2, height: "calc(100% - 120px)" }}>
          <Table
            rowData={rowData}
            rowCount={rowCount}
            loading={loading}
            columnDef={column}
            headerHeight={headerHeight}
            disableColumnMenu={true}
            paginationModel={paginationModel}
            onPaginationModelChange={(newPaginationModel: any) => {
              setPaginationModel(newPaginationModel);
            }}
            checkboxSelection={false}
            rowHeight={50}
          />
        </Box>
      </Box>
    </DialogContainer>
  );
}
